import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../PropertyPanelContext';
import RichEditorControl from '../CommonProperties/RichEditorControl';

const FixedBgTextProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 컨텍스트에서 필요한 값과 함수들 가져오기
  const context = usePropertyPanel() || {};
  const {
    devicePropertyTab = 'pc',
    getDevicePropertyValue = (source, field, defaultValue) => defaultValue,
    handleImageUpload = async () => null
  } = context;
  
  // 모바일 환경 확인
  const isMobile = devicePropertyTab === 'mobile';
  
  // 컴포넌트 속성 직접 변경 함수
  const updateComponentProps = (property, value) => {
    if (!selectedComponent) return;
    
    // 디바이스별 속성 처리를 위한 깊은 복사
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 속성만 변경
    if (devicePropertyTab) {
      // 디바이스별 속성 객체 초기화
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = { pc: {}, mobile: {} };
        
        // 기존 속성을 PC/모바일에 복사
        Object.keys(existingProps).forEach(key => {
          if (key !== 'deviceProperty') {
            existingProps.deviceProperty.pc[key] = existingProps[key];
            existingProps.deviceProperty.mobile[key] = existingProps[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      
      existingProps.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingProps[property] = value;
      }
    } else {
      // 디바이스 속성 탭이 선택되지 않은 경우 모든 디바이스에 적용
      existingProps[property] = value;
      
      // 디바이스별 속성도 같이 업데이트
      if (existingProps.deviceProperty) {
        if (existingProps.deviceProperty.pc) {
          existingProps.deviceProperty.pc[property] = value;
        }
        if (existingProps.deviceProperty.mobile) {
          existingProps.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // Redux 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  // 리치 에디터 저장 처리 함수
  const handleSaveContent = (content) => {
    if (!selectedComponent) return;
    
    // 디바이스 속성 탭이 선택된 경우 디바이스별로 저장
    if (devicePropertyTab) {
      const existingContent = selectedComponent.content ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        { deviceProperty: { pc: '', mobile: '' } };
      
      if (!existingContent.deviceProperty) {
        existingContent.deviceProperty = { pc: '', mobile: '' };
      }
      
      existingContent.deviceProperty[devicePropertyTab] = content;
      
      if (devicePropertyTab === 'pc') {
        existingContent.html = content;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: existingContent
        }
      }));
    } else {
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    }
  };
  
  // 현재 컨텐츠 가져오기
  const getCurrentContent = () => {
    if (!selectedComponent || !selectedComponent.content) return '';
    
    if (typeof selectedComponent.content === 'object') {
      if (selectedComponent.content.deviceProperty && 
          selectedComponent.content.deviceProperty[devicePropertyTab]) {
        return selectedComponent.content.deviceProperty[devicePropertyTab];
      } else if (selectedComponent.content.html) {
        return selectedComponent.content.html;
      }
    } else if (typeof selectedComponent.content === 'string') {
      return selectedComponent.content;
    }
    
    return '';
  };
  
  return (
    <>
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-image"></i> 배경 이미지 설정
        </div>
        <div className="property-control">
          <label style={{ minWidth: '30%' }}>배경 이미지</label>
          <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              value={getDevicePropertyValue(selectedComponent.componentProps, 'bgImageUrl', 'https://via.placeholder.com/1920x1080')} 
              onChange={(e) => updateComponentProps('bgImageUrl', e.target.value)}
              style={{ flex: 1 }}
            />
            <label className="upload-button" style={{ 
              padding: '8px 12px', 
              backgroundColor: '#4a90e2', 
              color: 'white', 
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              업로드
              <input 
                type="file" 
                accept="image/*" 
                onChange={async (e) => {
                  const imageUrl = await handleImageUpload(e);
                  if (imageUrl) {
                    updateComponentProps('bgImageUrl', imageUrl);
                  }
                }} 
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>
      </div>
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-palette"></i> 오버레이 설정
        </div>
        <div className="property-control">
          <label>오버레이 색상</label>
          <div className="color-picker-container">
            <input 
              type="color" 
              value={getDevicePropertyValue(selectedComponent.componentProps, 'overlayColor', '#000000')} 
              onChange={(e) => updateComponentProps('overlayColor', e.target.value)}
            />
          </div>
        </div>
        <div className="property-control">
          <label>오버레이 불투명도</label>
          <input 
            type="range" 
            min="0" 
            max="1" 
            step="0.1" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'overlayOpacity', 0.3)} 
            onChange={(e) => updateComponentProps('overlayOpacity', parseFloat(e.target.value))}
          />
          <span>{(getDevicePropertyValue(selectedComponent.componentProps, 'overlayOpacity', 0.3) * 100).toFixed(0)}%</span>
        </div>
      </div>
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-font"></i> 텍스트 설정
        </div>
        <div className="property-control">
          <label>텍스트 색상</label>
          <div className="color-picker-container">
            <input 
              type="color" 
              value={getDevicePropertyValue(selectedComponent.componentProps, 'textColor', '#ffffff')} 
              onChange={(e) => updateComponentProps('textColor', e.target.value)}
            />
          </div>
        </div>
        {/* <div className="property-control">
          <label>콘텐츠 정렬</label>
          <select 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'contentAlignment', 'center')} 
            onChange={(e) => updateComponentProps('contentAlignment', e.target.value)}
          >
            <option value="top">상단</option>
            <option value="center">중앙</option>
            <option value="bottom">하단</option>
          </select>
        </div> */}
        
        {/* RichEditorControl 컴포넌트 사용 */}
        <RichEditorControl 
          initialContent={getCurrentContent()}
          onContentSave={handleSaveContent}
          showPreview={true}
        />
      </div>
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-ruler"></i> 크기 설정
        </div>
        <div className="property-control">
          <label>높이 (px)</label>
          <input 
            type="number" 
            min="100" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'height', isMobile ? 400 : 500)} 
            onChange={(e) => updateComponentProps('height', parseInt(e.target.value))}
          />
        </div>
      </div>
    </>
  );
};

export default FixedBgTextProperties;